import { useState, useEffect, useRef, useCallback } from 'react';
import {
  appLifecycleApi,
  type AppLifecycleStatus,
  type AppServiceInfo,
} from '../services/appLifecycleApi';

export type AppStatus =
  | 'stopped'
  | 'starting'
  | 'running'
  | 'stopping'
  | 'partial'
  | 'error'
  | 'no_compose'
  | 'unknown';

export interface AppLifecycleState {
  status: AppStatus;
  services: AppServiceInfo[];
  hasCompose: boolean;
  appUrl: string | null;
  error: string | null;
}

const POLL_INTERVAL_TRANSITIONING = 20_000; // 20s while starting/stopping (aligned with backend cache TTL of 15s)
const POLL_INTERVAL_IDLE = 60_000; // 60s when running/stopped
const POLL_JITTER_RATIO = 0.1; // ±10% jitter per tick

const INITIAL_STATE: AppLifecycleState = {
  status: 'unknown',
  services: [],
  hasCompose: false,
  appUrl: null,
  error: null,
};

/**
 * Apply ±POLL_JITTER_RATIO jitter to the base interval so multiple tabs
 * opened simultaneously don't poll the backend in lockstep. The jitter is
 * re-rolled on every tick (we use a setTimeout chain rather than
 * setInterval) so concurrent pollers gradually decohere instead of just
 * being phase-shifted.
 */
const jittered = (ms: number): number => {
  const delta = ms * POLL_JITTER_RATIO;
  return Math.round(ms + (Math.random() * 2 - 1) * delta);
};

export function useAppLifecycle(projectId: string | undefined) {
  const [state, setState] = useState<AppLifecycleState>(INITIAL_STATE);
  const [actionError, setActionError] = useState<string | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  // Latest status held in a ref so the polling loop can pick the next
  // interval without re-subscribing the effect on every state change
  // (which would tear down/restart the timer chain on every tick and
  // defeat the jitter). The ref is updated from two places:
  //   1. The useEffect below (covers external state changes).
  //   2. fetchStatus itself (covers the synchronous read inside `tick`,
  //      which previously read this ref before React had a chance to
  //      flush the setState from the just-completed fetch — meaning the
  //      first poll after a transition picked the wrong cadence).
  const statusRef = useRef<AppStatus>(INITIAL_STATE.status);
  useEffect(() => {
    statusRef.current = state.status;
  }, [state.status]);

  // Imperative scheduler controls. Defined here as refs so startApp/stopApp
  // can trigger an immediate reschedule (without waiting up to IDLE_INTERVAL
  // for the in-flight 60s timer to fire) the moment we know the user just
  // initiated a transition.
  const clearScheduledRef = useRef<() => void>(() => {});
  const scheduleNextRef = useRef<() => void>(() => {});

  const isTransitioning = state.status === 'starting' || state.status === 'stopping';

  const fetchStatus = useCallback(async (): Promise<AppStatus | null> => {
    if (!projectId) return null;
    try {
      const data: AppLifecycleStatus = await appLifecycleApi.getStatus(projectId);
      if (!mountedRef.current) return null;

      const nextStatus = data.status as AppStatus;
      // Update the ref *synchronously* so the polling loop's `tick` —
      // which calls scheduleNext immediately after this resolves — sees
      // the freshly-fetched status, not the pre-fetch React state.
      statusRef.current = nextStatus;

      setState({
        status: nextStatus,
        services: data.services,
        hasCompose: data.has_compose,
        appUrl: data.app_url,
        error: data.error ?? null,
      });
      setActionError(null);
      return nextStatus;
    } catch {
      if (!mountedRef.current) return null;
      statusRef.current = 'no_compose';
      setState((prev) => ({
        ...prev,
        status: 'no_compose',
        hasCompose: false,
      }));
      return 'no_compose';
    }
  }, [projectId]);

  // Polling loop: setTimeout chain (not setInterval) so each tick uses a
  // freshly jittered delay. Pauses entirely when the tab is hidden.
  useEffect(() => {
    mountedRef.current = true;

    if (!projectId) {
      setState(INITIAL_STATE);
      return;
    }

    const clearScheduled = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const scheduleNext = () => {
      clearScheduled();
      if (typeof document !== 'undefined' && document.hidden) {
        // Don't burn polls while the user can't see the UI; the
        // visibilitychange handler resumes when the tab becomes active.
        return;
      }
      const transitioning =
        statusRef.current === 'starting' || statusRef.current === 'stopping';
      const base = transitioning
        ? POLL_INTERVAL_TRANSITIONING
        : POLL_INTERVAL_IDLE;
      timeoutRef.current = setTimeout(tick, jittered(base));
    };

    // Expose to startApp/stopApp so they can force an immediate
    // reschedule the moment the user initiates a transition, instead of
    // waiting up to IDLE_INTERVAL (60s) for the previously-scheduled
    // idle-cadence timer to fire.
    clearScheduledRef.current = clearScheduled;
    scheduleNextRef.current = scheduleNext;

    const tick = async () => {
      timeoutRef.current = null;
      await fetchStatus();
      if (!mountedRef.current) return;
      scheduleNext();
    };

    const handleVisibility = () => {
      if (typeof document === 'undefined') return;
      if (document.hidden) {
        clearScheduled();
      } else {
        // Coming back: refresh immediately, then resume scheduled polling.
        void (async () => {
          await fetchStatus();
          if (mountedRef.current) scheduleNext();
        })();
      }
    };

    void (async () => {
      await fetchStatus();
      if (mountedRef.current) scheduleNext();
    })();

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVisibility);
    }

    return () => {
      mountedRef.current = false;
      clearScheduled();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibility);
      }
    };
  }, [projectId, fetchStatus]);

  const beginTransition = useCallback((next: 'starting' | 'stopping') => {
    // Update the ref synchronously so the polling scheduler picks the
    // TRANSITIONING (20s) cadence on its very next call, before React
    // has flushed the setState below.
    statusRef.current = next;
    setState((prev) => ({ ...prev, status: next }));
    // Cancel the previously-scheduled (likely IDLE-cadence) timer and
    // reschedule using the new transition cadence. Without this the user
    // could wait up to POLL_INTERVAL_IDLE (60s) before the first poll
    // after clicking Start/Stop, regardless of statusRef.
    clearScheduledRef.current();
    scheduleNextRef.current();
  }, []);

  const startApp = useCallback(async () => {
    if (!projectId) return;
    setActionError(null);
    try {
      await appLifecycleApi.startApp(projectId);
      beginTransition('starting');
      // Kick off an immediate status fetch so the UI flips out of the cached
      // pre-start snapshot quickly (the backend invalidated its cache at the
      // same moment, so this read will reflect the new lock).
      // This fetch can race with the polling loop's next tick; the backend
      // cache + single-flight make duplicates effectively free, so we leave
      // it in for snappier UX rather than adding a frontend dedupe layer.
      setTimeout(fetchStatus, 1_000);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to start app';
      setActionError(message);
    }
  }, [projectId, fetchStatus, beginTransition]);

  const stopApp = useCallback(async () => {
    if (!projectId) return;
    setActionError(null);
    try {
      await appLifecycleApi.stopApp(projectId);
      beginTransition('stopping');
      setTimeout(fetchStatus, 1_000);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to stop app';
      setActionError(message);
    }
  }, [projectId, fetchStatus, beginTransition]);

  return {
    state,
    startApp,
    stopApp,
    refreshStatus: fetchStatus,
    isTransitioning,
    actionError,
  };
}
